import React, { Fragment, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Table, Spinner, Badge } from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Breadcrumbs } from '../../../../AbstractElements';
import API_BASE_URL from '../../../../Config/api';

const SyncShopifyImages = () => {
  const [shopUrl, setShopUrl] = useState('https://www.faraboutique.com');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [matchBy, setMatchBy] = useState('sku');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [lastWasDry, setLastWasDry] = useState(true);

  const run = async (dryRun) => {
    setBusy(true);
    setReport(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/import-shopify-images`, { shopUrl, onlyMissing, matchBy, dryRun });
      setReport(res.data);
      setLastWasDry(dryRun);
      toast.success(dryRun ? 'Preview complete' : `Imported ${res.data.imported} images`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to contact Shopify');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Products" title="Sync Shopify Images" />
      <Container fluid={true}>
        <Row>
          <Col xl="5">
            <Card>
              <CardHeader className="pb-0">
                <h5>Import product images from Shopify</h5>
                <span>Matches each product by SKU against your live store and pulls in its photos.</span>
              </CardHeader>
              <CardBody>
                <Label className="form-label">Store URL</Label>
                <Input className="mb-3" value={shopUrl} onChange={(e) => setShopUrl(e.target.value)} placeholder="https://www.faraboutique.com" />

                <Label className="form-label">Match products by</Label>
                <Input type="select" className="mb-1" value={matchBy} onChange={(e) => setMatchBy(e.target.value)}>
                  <option value="sku">SKU (exact)</option>
                  <option value="name">Name + colour (fallback)</option>
                </Input>
                <p className="text-muted f-12 mb-3">
                  {matchBy === 'sku'
                    ? 'Most reliable. Matches our SKU to the Shopify variant SKU.'
                    : 'For products whose SKU differs on Shopify. Matches by product name, then imports only when the colour matches exactly — never a guessed colour.'}
                </p>

                <div className="form-check mb-3">
                  <Input type="checkbox" id="onlyMissing" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
                  <Label for="onlyMissing" className="form-check-label">Only products that currently have no images</Label>
                </div>

                <div className="d-flex gap-2">
                  <Button color="secondary" outline disabled={busy} onClick={() => run(true)}>
                    {busy && lastWasDry ? <Spinner size="sm" /> : 'Preview'}
                  </Button>
                  <Button color="primary" disabled={busy} onClick={() => run(false)}>
                    {busy && !lastWasDry ? <Spinner size="sm" /> : 'Import images'}
                  </Button>
                </div>
                <p className="text-muted f-12 mb-0 mt-3">
                  <strong>Preview</strong> only reports what would change. <strong>Import</strong> writes the matched image URLs into the catalog.
                  Already-attached images are skipped, so it's safe to re-run.
                </p>
              </CardBody>
            </Card>
          </Col>

          <Col xl="7">
            <Card>
              <CardHeader className="pb-0">
                <h5>Result {report && <Badge color={lastWasDry ? 'secondary' : 'success'} className="ms-2">{lastWasDry ? 'Preview' : 'Imported'}</Badge>}</h5>
              </CardHeader>
              <CardBody>
                {busy ? (
                  <p className="text-muted"><Spinner size="sm" /> Contacting Shopify &amp; matching SKUs…</p>
                ) : !report ? (
                  <p className="text-muted mb-0">Run a preview to see how many products can be matched.</p>
                ) : (
                  <>
                    <Row className="text-center g-2 mb-3">
                      <Col><div className="h4 mb-0">{report.shopifyProducts}</div><div className="text-muted f-12">Shopify products</div></Col>
                      <Col><div className="h4 mb-0">{report.considered}</div><div className="text-muted f-12">Considered</div></Col>
                      <Col><div className="h4 mb-0 text-success">{report.matched}</div><div className="text-muted f-12">Matched</div></Col>
                      <Col><div className="h4 mb-0 text-primary">{report.imported}</div><div className="text-muted f-12">{lastWasDry ? 'Would import' : 'Imported'}</div></Col>
                      {report.matchBy === 'name' && (
                        <Col><div className="h4 mb-0 text-warning">{(report.colourMismatch || []).length}</div><div className="text-muted f-12">Colour mismatch</div></Col>
                      )}
                      <Col><div className="h4 mb-0 text-muted">{report.unmatched.length}</div><div className="text-muted f-12">{report.matchBy === 'name' ? 'No name match' : 'Unmatched'}</div></Col>
                    </Row>

                    {report.details.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: 280 }}>
                        <Table className="align-middle" style={{ fontSize: 13 }}>
                          <thead><tr><th>SKU</th><th>Product</th><th>Shopify match</th><th className="text-center">Images</th></tr></thead>
                          <tbody>
                            {report.details.map((d, i) => (
                              <tr key={i}>
                                <td className="text-muted">{d.sku}</td>
                                <td className="fw-semibold">{d.title}{d.colour ? <span className="text-muted"> · {d.colour}</span> : ''}</td>
                                <td>{d.shopifyTitle}{d.shopifyColour ? <span className="text-muted"> · {d.shopifyColour}</span> : ''}</td>
                                <td className="text-center">{d.imported}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {(report.colourMismatch || []).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-warning" style={{ cursor: 'pointer' }}>
                          {report.colourMismatch.length} name-matched but colour not available on Shopify (skipped — not imported)
                        </summary>
                        <div className="table-responsive mt-2" style={{ maxHeight: 220 }}>
                          <Table size="sm" style={{ fontSize: 12 }}>
                            <thead><tr><th>SKU</th><th>Our colour</th><th>Available on Shopify</th></tr></thead>
                            <tbody>
                              {report.colourMismatch.map((m, i) => (
                                <tr key={i}>
                                  <td className="text-muted">{m.sku}</td>
                                  <td>{m.colour || '—'}</td>
                                  <td className="text-muted">{(m.available || []).join(', ') || m.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </details>
                    )}

                    {report.unmatched.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-muted" style={{ cursor: 'pointer' }}>
                          {report.unmatched.length} {report.matchBy === 'name' ? 'with no matching product name on Shopify' : 'SKUs not found on Shopify (likely unpublished/sold out)'}
                        </summary>
                        <p className="text-muted f-12 mt-2 mb-0">{report.unmatched.join(', ')}</p>
                      </details>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default SyncShopifyImages;
